import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, TableCell, Tooltip, Typography } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { toast } from 'react-toastify';

const CLAMP_LINES = 3;

/**
 * Whether the clamped element is actually hiding anything, so a chunk that
 * already fits does not get a tooltip repeating text the admin can read.
 */
const useIsClamped = (text) => {
  const ref = useRef(null);
  const [isClamped, setIsClamped] = useState(false);

  const measure = useCallback(() => {
    const element = ref.current;
    if (!element) return;
    setIsClamped(element.scrollHeight > element.clientHeight + 1);
  }, []);

  useEffect(() => {
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    // Column widths change with the viewport, which changes what fits.
    const observer = new ResizeObserver(measure);
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [measure, text]);

  return { ref, isClamped };
};

const KnowledgePreviewCell = ({ text }) => {
  const fullText = String(text || '');
  const { ref, isClamped } = useIsClamped(fullText);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      toast.success('Full chunk text copied');
    } catch {
      toast.error('Could not copy the chunk text');
    }
  };

  if (!fullText) {
    return (
      <TableCell sx={{ verticalAlign: 'top', minWidth: 220 }}>
        <Typography variant='caption' color='text.disabled'>
          —
        </Typography>
      </TableCell>
    );
  }

  return (
    <TableCell sx={{ verticalAlign: 'top', color: 'text.secondary', minWidth: 220 }}>
      <Box display='flex' alignItems='flex-start' gap={0.5}>
        <Tooltip
          title={isClamped ? fullText : ''}
          placement='top'
          enterDelay={300}
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 560,
                // Long chunks scroll inside the tooltip rather than being cut
                // off again. MUI tooltips are hoverable, so this is reachable.
                maxHeight: '60vh',
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                fontSize: 13,
                lineHeight: 1.6,
              },
            },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, cursor: isClamped ? 'help' : 'inherit' }}>
            {/* The complete text stays in the DOM and is only clamped visually,
                so selecting and copying a row yields the whole chunk. */}
            <Box
              ref={ref}
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: CLAMP_LINES,
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {fullText}
            </Box>
          </Box>
        </Tooltip>
        <Tooltip title='Copy full text'>
          <IconButton
            size='small'
            onClick={handleCopy}
            aria-label='Copy full chunk text'
            sx={{ mt: -0.25, color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
          >
            <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </TableCell>
  );
};

export default KnowledgePreviewCell;
